import React, { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Banner sutil que aparece la primera vez que abrís Altiv en mobile
 * browser (no PWA), invitando a instalarla. Si el usuario descarta,
 * se acuerda y no vuelve a aparecer.
 *
 * Comportamiento:
 *  - Sólo en mobile.
 *  - Sólo si NO está corriendo como standalone (display-mode).
 *  - Sólo si el evento beforeinstallprompt llega (Chrome/Edge Android).
 *    iOS Safari nunca lo dispara — para iOS muestra hint estático con
 *    instrucciones de "Compartir → Agregar a inicio".
 *  - Persistencia: localStorage altiv_pwa_install_dismissed (timestamp);
 *    re-aparece después de 30 días si no se instaló.
 */
const STORAGE_KEY = "altiv_pwa_install_dismissed";
const COOLDOWN_DAYS = 30;

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true);

const isMobile = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;

const isIOS = () =>
  typeof window !== "undefined" && /iPad|iPhone|iPod/.test(window.navigator.userAgent || "");

export default function PWAInstallBanner() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isMobile() || isStandalone()) return;

    // Re-show cooldown
    try {
      const dismissed = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      if (dismissed && Date.now() - dismissed < COOLDOWN_DAYS * 86400_000) return;
    } catch {}

    // iOS Safari: no beforeinstallprompt; mostramos hint estático.
    if (isIOS()) {
      setShow(true);
      return;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {}
    setShow(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === "accepted") {
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch {}
    }
    setDeferredPrompt(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="sm:hidden fixed left-3 right-3 z-[57] bg-[#0f172a] border border-[#0052FF]/40 rounded-xl shadow-2xl p-3 flex items-start gap-3"
      style={{ bottom: "calc(80px + env(safe-area-inset-bottom, 0px))" }}
    >
      <div className="w-9 h-9 rounded-lg bg-[#0052FF]/20 flex items-center justify-center flex-shrink-0">
        <Smartphone className="w-5 h-5 text-[#4d8cff]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white">{t("pwa.installTitle")}</p>
        <p className="text-xs text-slate-400 mt-0.5 leading-snug">
          {isIOS() ? t("pwa.installIOS") : t("pwa.installAndroid")}
        </p>
        {!isIOS() && deferredPrompt && (
          <button
            onClick={install}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0052FF] text-white text-xs font-semibold active:bg-blue-600"
          >
            <Download className="w-3.5 h-3.5" />
            {t("pwa.installNow")}
          </button>
        )}
      </div>
      <button
        onClick={dismiss}
        aria-label="Cerrar"
        className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
